import { SavingRegion } from '@common/components/SavingRegion';
import { getBookingRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { useTranslations } from '@lang';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { Extras, Price } from 'shared-types/Common';
import { ExtrasTemplate } from 'voyager';
import type { ExtrasEditResult } from 'voyager';
import { RoomRateFormValues } from '../../RoomRateForm.schema';
import { RoomRateFormStepNames } from '../constants';
import { priceWithExtras } from '../ExtrasStep/utils';
import { GuestDetailsSelectedEntity } from '../GuestDetailsStepV2/GuestDetailsStepV2.types';
import { findSelectedEntity } from '../selectedEntity.helper';
import { ExtrasStepV2Props } from './ExtrasStepV2.types';
import { useRoomExtrasLabels } from './useRoomExtrasLabels';
import {
  ExtraFormEntry,
  ExtraOptionSelectionState,
  buildExtrasBasketPayload,
  buildExtrasGuests,
  buildExtrasItems,
  editResultPatch,
  guestSelectionPatch,
} from './utils';

export const ExtrasStepV2 = ({
  metaEntity,
  entity,
  availableExtras,
  addToBasket,
  goToNextStep,
  goToPreviousStep,
  isFetching,
  language,
}: ExtrasStepV2Props) => {
  const { t } = useTranslations();
  const router = useRouter();
  const form = useFormContext<RoomRateFormValues>();
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = form;

  const guestValues = watch('guests') ?? [];
  const extrasValues = (watch('extras') as (ExtraFormEntry | undefined)[] | undefined) ?? [];

  const optionSelections = useMemo(() => {
    const map: Record<number, ExtraOptionSelectionState | undefined> = {};
    extrasValues.forEach((entry, index) => {
      if (entry?.optionSelections) {
        map[index] = entry.optionSelections;
      }
    });
    return map;
  }, [extrasValues]);

  const guestSelections = useMemo(() => {
    const map: Record<number, string[] | undefined> = {};
    extrasValues.forEach((entry, index) => {
      if (entry?.guestSelections) {
        map[index] = entry.guestSelections;
      }
    });
    return map;
  }, [extrasValues]);

  const areGuestsLoaded = guestValues.some(
    (guest) => guest.firstName || guest.lastName || guest.title
  );

  useEffect(() => {
    if (areGuestsLoaded) {
      return;
    }
    router.push({
      pathname: getBookingRoute(router.pathname, metaEntity.type),
      query: {
        id: metaEntity.id,
        bookingStep: RoomRateFormStepNames.GUESTS,
        [entity.type]: entity.id,
      },
    });
  }, [areGuestsLoaded, router, metaEntity.type, metaEntity.id, entity.type, entity.id]);

  const selectedEntity = findSelectedEntity(metaEntity, entity.id) as
    | GuestDetailsSelectedEntity
    | undefined;

  const extras = useMemo(() => availableExtras ?? [], [availableExtras]);

  const guests = useMemo(
    () =>
      buildExtrasGuests(guestValues, (guestNumber) =>
        t('room.guestDetails.guestTitle', { number: guestNumber })
      ),
    [guestValues, t]
  );

  const currencyCode =
    selectedEntity?.pricePerStay?.currencyCode ?? extras[0]?.price?.currencyCode ?? 'GBP';

  const items = useMemo(
    () =>
      buildExtrasItems({
        extras,
        entries: extrasValues,
        optionSelections,
        guestSelections,
        language,
        fallbackCurrencyCode: currencyCode,
      }),
    [extras, extrasValues, optionSelections, guestSelections, language, currencyCode]
  );

  const setExtraEntry = (index: number, patch: Partial<ExtraFormEntry>) => {
    const current = (form.getValues('extras') as (ExtraFormEntry | undefined)[] | undefined) ?? [];
    const updated = [...current];
    updated[index] = { ...updated[index], ...patch };
    setValue('extras', updated as RoomRateFormValues['extras'], { shouldValidate: true });
  };

  const handleAdd = (itemId: string) => {
    setExtraEntry(Number(itemId), { isSelected: true });
  };

  const handleRemove = (itemId: string) => {
    setExtraEntry(Number(itemId), { isSelected: false });
  };

  const handleConfirmGuestSelection = (itemId: string, selectedGuestIds: string[]) => {
    setExtraEntry(Number(itemId), guestSelectionPatch(selectedGuestIds));
  };

  const handleConfirmSelection = (itemId: string, result: ExtrasEditResult) => {
    const index = Number(itemId);
    setExtraEntry(index, editResultPatch(extras[index], result, guests.length));
  };

  const displayPrice = useMemo<Price | null>(
    () =>
      selectedEntity?.pricePerStay
        ? priceWithExtras(
            selectedEntity as { pricePerStay: Price },
            extras as unknown as Extras[],
            extrasValues.map((entry) => entry ?? {})
          )
        : null,
    [selectedEntity, extras, extrasValues]
  );

  const onSubmit = async (values: RoomRateFormValues) => {
    const extrasPayload = buildExtrasBasketPayload(
      extras,
      (values.extras as (ExtraFormEntry | undefined)[] | undefined) ?? [],
      optionSelections
    );
    await addToBasket({ guestDetails: values.guests, extras: extrasPayload, note: values.note });
    goToNextStep();
  };

  const sharedLabels = useRoomExtrasLabels(language);
  const labels = useMemo(
    () => ({
      ...sharedLabels,
      footerTitle: metaEntity.title ?? sharedLabels.title,
      ctaLabel: t('booking.continue'),
    }),
    [sharedLabels, metaEntity.title, t]
  );

  return (
    <SavingRegion isSaving={isFetching}>
      <ExtrasTemplate
        items={items}
        guests={guests.length > 1 ? guests : undefined}
        currentStep={2}
        totalSteps={2}
        totalAmount={displayPrice?.amount ?? 0}
        currencyCode={currencyCode}
        locale={language}
        onBack={goToPreviousStep}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onConfirmGuestSelection={handleConfirmGuestSelection}
        onConfirmSelection={handleConfirmSelection}
        onCtaClick={handleSubmit(onSubmit)}
        ctaDisabled={!isValid || isSubmitting || isFetching}
        labels={labels}
      />
    </SavingRegion>
  );
};
